import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DatePicker, Button, Space, Tooltip } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { ahoraChile } from "../../utils/fechaChile";
import { CADUCIDAD_FECHAS_DIAS } from "../../hooks/filtrosPersistidos";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

type Modo = "mes" | "rango" | "anterior" | "todos";

export interface FiltroFecha {
  modo: Modo;
  mes?: Dayjs;
  rango?: [Dayjs, Dayjs];
}

type Props = {
  value?: FiltroFecha; // ✅ controlado
  defaultValue?: FiltroFecha; // ✅ no-controlado (opcional)
  onChange: (f: FiltroFecha) => void;
  /**
   * Si es true, oculta el selector de rango "Desde/Hasta" y deja SOLO el modo
   * mensual (MonthPicker + "mes anterior"/"mes actual"). Se usa en
   * `/finanzas-empresa`, cuyo cierre es mensual y cuya atribución de arriendos
   * es por mes de check-in (ver docs/flujo-financiero.md §1 regla 8). En
   * `/arriendos` se deja en false (mantiene el rango para exploración).
   */
  monthlyOnly?: boolean;
  /**
   * Si es true, muestra el botón "Todos" (modo `todos`): sin ventana de fecha,
   * lista TODO el histórico sin filtrar por mes. Solo se activa en `/arriendos`
   * (tabla operativa). En `/finanzas-empresa` se deja en false: su reporte es
   * mensual y "todos" reventaría los agregados/series por mes.
   */
  allowTodos?: boolean;
};

export function FiltroGlobalFecha({
  value,
  defaultValue,
  onChange,
  monthlyOnly = false,
  allowTodos = false,
}: Props) {
  const isControlled = value != null;

  // Estado interno (solo se usa cuando NO es controlado)
  const [modo, setModo] = useState<Modo>("mes");
  const [mes, setMes] = useState<Dayjs>(ahoraChile());
  const [rango, setRango] = useState<[Dayjs, Dayjs] | null>(null);

  // Fuente de verdad para render: value si es controlado, si no el estado interno
  const current: FiltroFecha = useMemo(() => {
    if (isControlled) {
      // En controlado, si faltan campos por error, caemos a algo seguro
      if (value!.modo === "todos") return { modo: "todos" };
      if (value!.modo === "rango") {
        const r = value!.rango ?? [ahoraChile(), ahoraChile()];
        return { modo: "rango", rango: r };
      }
      const m = value!.mes ?? ahoraChile();
      return { modo: value!.modo, mes: m };
    }

    if (modo === "todos") return { modo: "todos" };
    if (modo === "rango") {
      return { modo: "rango", rango: rango ?? [ahoraChile(), ahoraChile()] };
    }
    return { modo, mes };
  }, [isControlled, value, modo, mes, rango]);

  // ✅ Al montar: SOLO inicializar si NO es controlado
  useEffect(() => {
    if (isControlled) return;

    const initial =
      defaultValue ?? ({ modo: "mes", mes: ahoraChile() } as FiltroFecha);

    setModo(initial.modo);
    if (initial.modo === "rango") {
      setRango(initial.rango ?? null);
      setMes(ahoraChile());
    } else {
      setMes(initial.mes ?? ahoraChile());
      setRango(null);
    }

    // Mantiene el comportamiento anterior, pero sin pisar cuando es controlado
    onChange(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Si pasa a controlado o cambia value, sincronizamos UI interna (solo para que MonthPicker/RangePicker reflejen bien)
  useEffect(() => {
    if (!isControlled) return;
    // Si te interesa mantener estado interno coherente (no es estrictamente necesario),
    // sincronizamos sin disparar onChange.
    const v = value!;
    if (v.modo === "rango") {
      setModo("rango");
      setRango(v.rango ?? null);
    } else {
      setModo(v.modo);
      setMes(v.mes ?? ahoraChile());
      setRango(null);
    }
  }, [isControlled, value]);

  const emit = (next: FiltroFecha) => {
    if (!isControlled) {
      setModo(next.modo);
      if (next.modo === "rango") {
        setRango(next.rango ?? null);
      } else {
        setMes(next.mes ?? ahoraChile());
        setRango(null);
      }
    }
    onChange(next);
  };

  return (
    <Space>
      <DatePicker.MonthPicker
        value={
          current.modo === "mes" || current.modo === "anterior"
            ? (current.mes ?? null)
            : null
        }
        disabled={current.modo === "rango"}
        onChange={(v) => {
          if (!v) return;
          emit({ modo: "mes", mes: v });
        }}
        placeholder="Seleccionar mes"
      />

      {!monthlyOnly && (
        <DatePicker.RangePicker
          value={current.modo === "rango" ? (current.rango as any) : null}
          onChange={(v) => {
            if (!v) {
              const mesActual = ahoraChile();
              emit({ modo: "mes", mes: mesActual });
              return;
            }
            emit({ modo: "rango", rango: v as [Dayjs, Dayjs] });
          }}
          placeholder={["Desde", "Hasta"]}
        />
      )}

      {allowTodos && (
        <Tooltip title="Mostrar todos los arriendos, sin filtrar por fecha">
          <Button
            type={current.modo === "todos" ? "primary" : "default"}
            onClick={() => emit({ modo: "todos" })}
          >
            Todos
          </Button>
        </Tooltip>
      )}

      <Tooltip title="Ver mes anterior">
        <Button
          onClick={() => {
            const mesAnterior = ahoraChile().subtract(1, "month");
            emit({ modo: "anterior", mes: mesAnterior });
          }}
        >
          Ver mes anterior
        </Button>
      </Tooltip>

      {current.mes && !current.mes.isSame(ahoraChile(), "month") && (
        <Tooltip title="Ver mes actual">
          <Button
            type="primary"
            onClick={() => emit({ modo: "mes", mes: ahoraChile() })}
          >
            Ver mes actual
          </Button>
        </Tooltip>
      )}
    </Space>
  );
}

// ── Recordar el período elegido (round 2026-09-24) ───────────────────────
// Cuatro páginas usan este filtro (/arriendos, /finanzas-empresa, /flujos y
// /sueldos) y todas volvían al mes actual en cada recarga. Los Dayjs no
// sobreviven a JSON, así que se guarda "YYYY-MM" / "YYYY-MM-DD" y se
// reconstruyen al leer. Caduca a los 7 días porque el default de las cuatro
// es «el mes actual»: restaurar un mes viejo muestra otra cosa sin avisar.
// Ver docs/filtros-persistentes.md.

const MODOS: Modo[] = ["mes", "rango", "anterior", "todos"];
const ES_MES = /^\d{4}-\d{2}$/;
const ES_DIA = /^\d{4}-\d{2}-\d{2}$/;

/** "2026-09" → Dayjs, o undefined si lo guardado no sirve. */
const textoAMes = (crudo: unknown): Dayjs | undefined => {
  if (typeof crudo !== "string" || !ES_MES.test(crudo)) return undefined;
  const m = dayjs(`${crudo}-01`);
  // isValid() no alcanza: dayjs("2026-13") no falla, se corre de año.
  return m.isValid() && m.format("YYYY-MM") === crudo ? m : undefined;
};

const textoADia = (crudo: unknown): Dayjs | undefined => {
  if (typeof crudo !== "string" || !ES_DIA.test(crudo)) return undefined;
  const d = dayjs(crudo);
  return d.isValid() && d.format("YYYY-MM-DD") === crudo ? d : undefined;
};

export const serializarFiltroFecha = (f: FiltroFecha): unknown => ({
  modo: f.modo,
  mes: f.mes && f.mes.isValid() ? f.mes.format("YYYY-MM") : null,
  rango: f.rango
    ? [
        f.rango[0] && f.rango[0].isValid() ? f.rango[0].format("YYYY-MM-DD") : null,
        f.rango[1] && f.rango[1].isValid() ? f.rango[1].format("YYYY-MM-DD") : null,
      ]
    : null,
});

export const deserializarFiltroFecha = (
  crudo: unknown,
): FiltroFecha | undefined => {
  if (!crudo || typeof crudo !== "object") return undefined;
  const guardado = crudo as { modo?: unknown; mes?: unknown; rango?: unknown };
  const modo = MODOS.find((m) => m === guardado.modo);
  if (!modo) return undefined;
  if (modo === "todos") return { modo };
  if (modo === "rango") {
    if (!Array.isArray(guardado.rango) || guardado.rango.length !== 2) {
      return undefined;
    }
    const desde = textoADia(guardado.rango[0]);
    const hasta = textoADia(guardado.rango[1]);
    if (!desde || !hasta) return undefined;
    return { modo, rango: [desde, hasta] };
  }
  const mes = textoAMes(guardado.mes);
  return mes ? { modo, mes } : undefined;
};

/**
 * El período elegido, recordado por usuario admin en este navegador.
 * `restaurar: false` cuando la URL trae un enlace directo (?mes=, ?from/?to):
 * ese enlace manda sobre lo guardado.
 */
export function useFiltroFechaPersistido(
  clave: string,
  inicial: FiltroFecha | (() => FiltroFecha),
  opciones: { restaurar?: boolean } = {},
): [
  FiltroFecha,
  Dispatch<SetStateAction<FiltroFecha>>,
  Dispatch<SetStateAction<FiltroFecha>>,
] {
  return useFiltroPersistido<FiltroFecha>(clave, inicial, {
    caducaEnDias: CADUCIDAD_FECHAS_DIAS,
    serializar: serializarFiltroFecha,
    deserializar: deserializarFiltroFecha,
    restaurar: opciones.restaurar,
  });
}
